import { Theme } from 'src/ui/style/Theme'
import { css } from 'src/ui/style/css'
import { mediaMaxWidth } from 'src/ui/style/mixins/helpers'

export const equalColumns = (spacing = '0') => css`
    align-items: flex-start;
    > * {
        flex: 1 0 0%;
    }
    // Space between
    justify-content: space-between;
    ${horizontalChildSpacing(spacing)};
`

export const columnsLayout = ({
    spacing,
    breakpoint,
    breakpointSpacing
}: {
    spacing: string
    breakpoint: string
    breakpointSpacing?: string
}) => (th: Theme) =>
    css`
        ${equalColumns(spacing)};
        // Set to vertical layout on breakpoint
        ${mediaMaxWidth(breakpoint)} {
            flex-direction: column;
            align-items: stretch;
            ${horizontalChildSpacing('0')};
            ${verticalChildSpacing(breakpointSpacing || spacing)(th)};
        }
    `

export const grid = ({
    minWidth,
    maxWidth = '1fr',
    gap,
    auto = 'fit',
    autoRows = false
}: {
    minWidth: string
    maxWidth?: string
    gap?: string
    auto?: 'fit' | 'fill'
    autoRows?: boolean
}) => css`
    display: grid;
    grid-template-columns: repeat(
        ${`auto-${auto}`},
        minmax(${minWidth}, ${maxWidth})
    );
    ${gap && `grid-gap: ${gap};`}
    // Rows have same height according to highest row
    ${autoRows && 'grid-auto-rows: 1fr;'}
`

export const verticalChildSpacing = (spacing: string) => (th: Theme) =>
    css`
        > *:not(:last-child) {
            margin-bottom: ${spacing};
        }
    `
export const horizontalChildSpacing = (spacing: string) => css`
    > *:not(:last-child) {
        margin-right: ${spacing};
    }
`

export const verticalChildSpacingBreakpoint = ({
    spacing,
    breakpoint,
    breakpointSpacing
}: {
    spacing: string
    breakpoint: string
    breakpointSpacing: string
}) => css`
    > * {
        margin-bottom: ${spacing};
    }
    ${mediaMaxWidth(breakpoint)} {
        > * {
            margin-bottom: ${breakpointSpacing};
        }
    }
`

export const horizontalLayout = ({
    spacing,
    breakpoint,
    breakpointSpacing
}: {
    spacing: string
    breakpoint: string
    breakpointSpacing: string
}) => css`
    ${horizontalChildSpacing(spacing)};
    ${mediaMaxWidth(breakpoint)} {
        ${horizontalChildSpacing(breakpointSpacing)};
    }
`

export const verticalLayout = ({
    spacing,
    breakpoint,
    breakpointSpacing
}: {
    spacing: string
    breakpoint: string
    breakpointSpacing: string
}) => (th: Theme) =>
    css`
        ${verticalChildSpacing(spacing)(th)};
        ${mediaMaxWidth(breakpoint)} {
            ${verticalChildSpacing(breakpointSpacing)(th)};
        }
    `

// Applies to top level layout only
export const centeredContent = () => (th: Theme) =>
    css`
        width: 100%;
        max-width: ${th.containerMaxWidth};
        margin: 0 auto;
    `

const desktopPadding = '3em'
const mobilePadding = '1em'
export const horizontalPaddedContent = () => (th: Theme) =>
    css`
        padding-left: ${desktopPadding};
        padding-right: ${desktopPadding};
        ${mediaMaxWidth(th.screens.md)} {
            // Smaller padding on mobile screens
            padding-left: ${mobilePadding};
            padding-right: ${mobilePadding};
        }
    `

export const verticalPaddedContent = () => (th: Theme) =>
    css`
        padding-top: ${desktopPadding};
        padding-bottom: ${desktopPadding};
        ${mediaMaxWidth(th.screens.md)} {
            // Smaller padding on mobile screens
            padding-top: ${mobilePadding};
            padding-bottom: ${mobilePadding};
        }
    `

// Applies to top level layout only
export const paddedContent = () => (th: Theme) =>
    css`
        ${horizontalPaddedContent()(th)};
        ${verticalPaddedContent()(th)};
    `
